import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
} from "@xyflow/react";
import type { ReactFlowInstance } from "@xyflow/react";
import * as React from "react";
import "@xyflow/react/dist/style.css";
import { blastRadius, highlightsFor, type HighlightKind } from "./analysis";
import { FloatingEdge } from "./FloatingEdge";
import { autoLayout, inferDirection, type LayoutDirection } from "./layout";
import { NodePanel } from "./NodePanel";
import { isSourceNodeId, sourceIdFromNodeId } from "./sources";
import { flowToRf, rfToFlow, sourcesToRf, SOURCE_NODE_TYPE } from "./transforms";
import type { FlowDefinition, NodeDefinition, ThinkerOption } from "./types";
import "./styles.css";

export interface FlowCanvasEditorProps {
  /** Initial/canonical flow to render. The editor re-hydrates whenever this
   *  object reference changes (e.g. host swaps it for the server echo after a
   *  successful save, or reloads it on conflict). */
  flow: FlowDefinition;
  /** Thinker options for the ownerRole dropdown (host fetches them). */
  thinkers: ThinkerOption[];
  /** Whether a host-side save is in flight (drives the Save button label). */
  saving?: boolean;
  /** Inline error banner text (host surfaces save/validation errors here). */
  error?: string | null;
  /** Initial dirty state — true for scaffolded/new flows. Defaults to false. */
  initialDirty?: boolean;
  /** Fired whenever the dirty flag flips so the host can reflect it. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Fired when the user clicks Save. The core computes `merged` via rfToFlow
   *  and hands it to the host, which persists it (and typically swaps the
   *  `flow` prop for the server echo afterward). */
  onSave: (merged: FlowDefinition) => void;

  // ── embedding in a host that owns the editing surface ────────────────────────
  // Everything below is optional, so the VS Code webview and the TomatoFlow settings
  // editor keep their current behaviour by passing none of it. KnowIt's flow studio
  // uses them to run ONE editor: the canvas owns graph structure, the host owns
  // content and selection, and there is a single Save.

  /**
   * Re-hydration key. When provided, the canvas rebuilds its graph from `flow` only when this
   * value changes — NOT on every new `flow` object identity. That is what lets a host hold the
   * definition in its own draft state (and pass a fresh object on each keystroke) without the
   * canvas resetting under the author's hands. The host bumps it for its *own* structural edits
   * (a transition added from a form, a source added, a reload, a save echo) and leaves it alone
   * for edits that originated on the canvas.
   */
  revision?: number | string;
  /** Hide the built-in right rail — the host renders its own, unified inspector. */
  hideNodePanel?: boolean;
  /** Hide the built-in toolbar — the host provides title, Save and dirty state. */
  hideToolbar?: boolean;
  /**
   * Hide only the built-in Save, keeping Auto arrange and + Node. For a host that saves the whole
   * definition itself: two Save buttons over one definition is the duplication this prop exists to
   * avoid, but the structural actions still belong on the canvas.
   */
  hideSave?: boolean;
  /** Controlled selection. When provided, the canvas reflects it instead of owning it. */
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  selectedSourceId?: string | null;
  /** Fired on every selection change, including deselection (all fields null). */
  onSelect?: (selection: {
    nodeId: string | null;
    edgeId: string | null;
    sourceId: string | null;
  }) => void;
  /**
   * Fired after every canvas-originated structural change, with the merged definition. A host
   * that keeps its own draft should treat only the structural parts as authoritative (which nodes
   * exist, their transitions, the layout) and overlay its own content — see
   * `applyCanvasStructure` in KnowIt's FlowEditor.
   */
  onChange?: (merged: FlowDefinition) => void;
  /**
   * Dragging a connection out of a source node declares a consumer rather than a transition —
   * sources are inputs, not steps. The host owns the `sources` array, so it applies the change.
   */
  onSourceConnect?: (sourceId: string, nodeId: string) => void;
  /**
   * Nodes to mark as affected, keyed by why (`downstream`, `consumer`, `reads`, `problem`). This
   * is the blast radius drawn on the graph. When omitted, it is derived from the selected node so
   * the affordance exists even for hosts that do not compute it.
   */
  highlights?: Record<string, HighlightKind>;
  /** Extra buttons for the built-in toolbar (ignored when `hideToolbar`). */
  toolbarExtras?: React.ReactNode;
}

interface FlowNodeData {
  label: string;
  ownerRole: string;
  requireApproval: boolean;
  // ── Weave (15 §6.1): the same two facts the run canvas shows, shown while authoring ──
  kind?: string;
  tileKind?: string;
  tileTitle?: string;
  tileReview?: string;
  tileAssignee?: string;
  mechanics?: boolean;
  maxParallel?: number;
  [key: string]: unknown;
}

/** One glyph per executor kind — identical to the run canvas, so both read the same. */
const EDITOR_KIND_GLYPH: Record<string, string> = {
  compute: "⚙",
  reasoning: "◇",
  work: "▦",
  human: "☗",
  fork: "⑂",
  join: "⑃",
  forEach: "↻",
  subflow: "⤷",
};

/** Kind is a GLYPH; colour is reserved for state, which an unrun flow does not have. */
const EDITOR_TILE_GLYPH: Record<string, string> = {
  epic: "◆",
  feature: "▣",
  story: "▤",
  task: "▢",
  bug: "✕",
  issue: "◇",
  spike: "△",
  incident: "⚠",
};

// Current layout orientation, shared with the custom node renderer so its
// connection handles sit on the correct sides (left/right for LR, top/bottom
// for TB) — otherwise LR edges would awkwardly route from the bottom of one
// node to the top of the next.
const LayoutDirectionContext = React.createContext<LayoutDirection>("TB");

// Custom node renderer — shows persona/role and approval gate at a glance.
// Handles are REQUIRED for React Flow to anchor edges.
function FlowNodeView({ data, selected }: NodeProps) {
  const d = data as FlowNodeData;
  const dir = React.useContext(LayoutDirectionContext);
  const targetPos = dir === "LR" ? Position.Left : Position.Top;
  const sourcePos = dir === "LR" ? Position.Right : Position.Bottom;
  return (
    <div className={`fn-node ${selected ? "selected" : ""} ${d.requireApproval ? "approval" : ""}`}>
      <Handle type="target" position={targetPos} className="fn-handle" />
      <div className="fn-node-label">
        {d.kind ? (
          <span className="fn-node-executor" title={d.kind}>
            {EDITOR_KIND_GLYPH[d.kind] ?? "◈"}
          </span>
        ) : null}
        {d.label}
      </div>
      {d.tileKind ? (
        <div
          className={`fn-node-tile cat-proposed${d.tileReview === "gate" ? " gated" : ""}`}
          title={
            `${d.tileKind}${d.tileTitle ? `: ${d.tileTitle}` : ""}` +
            (d.tileAssignee ? `\nworked by ${d.tileAssignee}` : "") +
            (d.tileReview ? `\nreview: ${d.tileReview}` : "")
          }
        >
          <span className="fn-node-tile-glyph">{EDITOR_TILE_GLYPH[d.tileKind] ?? "▢"}</span>
          <span className="fn-node-tile-key">{d.tileKind}</span>
          {d.tileReview === "gate" ? <span className="fn-node-tile-state">gate</span> : null}
        </div>
      ) : null}
      <div className="fn-node-meta">
        <span className="fn-node-role">
          {d.tileAssignee ? d.tileAssignee : d.mechanics ? "mechanics" : d.ownerRole || "no role"}
        </span>
        {d.maxParallel != null && (
          <span className="fn-node-gate" title="branches at once">
            ‖{d.maxParallel}
          </span>
        )}
        {d.requireApproval && <span className="fn-node-gate">approval-gated</span>}
      </div>
      <Handle type="source" position={sourcePos} className="fn-handle" />
    </div>
  );
}

/**
 * A declared source: where data enters the flow. Drawn deliberately unlike a step — a source is
 * not executed, it is read — so the graph answers "what is this flow looking at?" at a glance
 * instead of starting mid-air at the first prompt.
 */
function SourceNodeView({ data, selected }: NodeProps) {
  const d = data as { label: string; kind: string };
  const dir = React.useContext(LayoutDirectionContext);
  const sourcePos = dir === "LR" ? Position.Right : Position.Bottom;
  return (
    <div className={`fn-source-node ${selected ? "selected" : ""}`}>
      <div className="fn-source-kind">{d.kind}</div>
      <div className="fn-source-label">{d.label}</div>
      <Handle type="source" position={sourcePos} className="fn-handle" />
    </div>
  );
}

const nodeTypes = {
  flowNode: FlowNodeView,
  [SOURCE_NODE_TYPE]: SourceNodeView,
};
const edgeTypes = { floating: FloatingEdge };

/**
 * Presentational, host-agnostic flow editor: toolbar (+ Node, Save), the
 * ReactFlow canvas, graph state (nodes/edges/selection/dirty), and the
 * NodePanel right rail. It performs NO network I/O — the host loads the flow,
 * fetches thinkers, and persists the merged FlowDefinition handed back via
 * onSave.
 */
export function FlowCanvasEditor({
  flow: flowProp,
  thinkers,
  saving = false,
  error = null,
  initialDirty = false,
  onDirtyChange,
  onSave,
  revision,
  hideNodePanel = false,
  hideToolbar = false,
  hideSave = false,
  selectedNodeId: selectedNodeIdProp,
  selectedEdgeId: selectedEdgeIdProp,
  selectedSourceId: selectedSourceIdProp,
  onSelect,
  onChange,
  onSourceConnect,
  highlights,
  toolbarExtras,
}: FlowCanvasEditorProps) {
  const [flow, setFlow] = React.useState<FlowDefinition>(flowProp);
  const [nodes, setNodes] = React.useState<Node[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = React.useState<string | null>(null);
  const [dirty, setDirtyState] = React.useState(initialDirty);
  const [direction, setDirection] = React.useState<LayoutDirection>("TB");
  const overrides = React.useRef<Map<string, Partial<NodeDefinition>>>(new Map());

  const onDirtyChangeRef = React.useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onSelectRef = React.useRef(onSelect);
  onSelectRef.current = onSelect;
  const flowPropRef = React.useRef(flowProp);
  flowPropRef.current = flowProp;

  /**
   * Set on every canvas-originated mutation, cleared once the change has been emitted. Piggybacks
   * on `setDirty(true)`, which every mutation already calls, so a new mutation handler cannot
   * forget to notify the host.
   */
  const shouldEmit = React.useRef(false);

  const setDirty = React.useCallback((value: boolean) => {
    if (value) {
      shouldEmit.current = true;
    }
    setDirtyState((prev) => {
      if (prev !== value) {
        onDirtyChangeRef.current?.(value);
      }
      return value;
    });
  }, []);

  // (Re)hydrate the canvas when the host says to. Without `revision` that means whenever the
  // canonical flow object identity changes (initial mount, server echo after save, conflict
  // reload) — the original behaviour. With `revision`, only when that value changes, so a host
  // holding the definition in its own draft can pass a fresh object on every keystroke without
  // the graph resetting mid-edit.
  const hydrationKey = revision === undefined ? flowProp : revision;
  React.useEffect(() => {
    const source = flowPropRef.current;
    overrides.current = new Map();
    setFlow(source);
    const graph = flowToRf(source);
    const sources = sourcesToRf(source);
    const rfNodes = [...graph.nodes, ...sources.nodes];
    setNodes(rfNodes);
    setEdges([...graph.edges, ...sources.edges]);
    // Match handle orientation to however this flow was last arranged.
    setDirection(inferDirection(graph.nodes));
    // Selection is the host's when controlled — resetting it here would fight the host's own
    // "select the node I just added" behaviour.
    if (selectedNodeIdProp === undefined) {
      setSelectedNodeId(null);
    }
    if (selectedEdgeIdProp === undefined) {
      setSelectedEdgeId(null);
    }
    setDirty(initialDirty);
    // A rehydrate is not a user edit: never echo it back to the host as a change.
    shouldEmit.current = false;
    // initialDirty and the controlled-selection flags are intentionally read once per hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationKey, setDirty]);

  // Emit canvas-originated structural changes once React has applied them.
  React.useEffect(() => {
    if (!shouldEmit.current) {
      return;
    }
    shouldEmit.current = false;
    onChangeRef.current?.(rfToFlow(flow, nodes, edges, overrides.current));
  }, [nodes, edges, flow]);

  const onNodesChange = React.useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
      // applyNodeChanges only removes nodes — sweep incident edges so the next
      // save doesn't emit dangling transitions.
      const removedIds = changes
        .filter((c): c is NodeChange & { type: "remove"; id: string } => c.type === "remove")
        .map((c) => c.id);
      if (removedIds.length > 0) {
        const removedSet = new Set(removedIds);
        setEdges((eds) =>
          eds.filter((e) => !removedSet.has(e.source) && !removedSet.has(e.target)),
        );
        removedIds.forEach((id) => overrides.current.delete(id));
        if (selectedNodeId && removedSet.has(selectedNodeId)) {
          setSelectedNodeId(null);
        }
      }
      if (changes.some((c) => c.type === "position" || c.type === "remove" || c.type === "add")) {
        setDirty(true);
      }
    },
    [selectedNodeId, setDirty],
  );

  const onEdgesChange = React.useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
      if (changes.some((c) => c.type === "remove" || c.type === "add")) {
        setDirty(true);
      }
    },
    [setDirty],
  );

  const onConnect = React.useCallback(
    (params: Connection) => {
      // Out of a source node this is not a transition: it declares that the target node reads
      // that source. The host owns the `sources` array, so hand it over and draw nothing here —
      // the edge comes back when the host re-hydrates with the new consumer.
      if (params.source && isSourceNodeId(params.source)) {
        if (params.target) {
          onSourceConnect?.(sourceIdFromNodeId(params.source), params.target);
        }
        return;
      }
      // Into a source node is meaningless — a source has no inputs.
      if (params.target && isSourceNodeId(params.target)) {
        return;
      }
      setEdges((eds) =>
        addEdge({ ...params, label: "always", animated: true, type: "floating" }, eds),
      );
      setDirty(true);
    },
    [setDirty, onSourceConnect],
  );

  /** One place that turns "this element is selected" into the host's selection shape. */
  const announce = React.useCallback((elementId: string | null, kind: "node" | "edge") => {
    const isSource = !!elementId && kind === "node" && isSourceNodeId(elementId);
    const nodeId = kind === "node" && !isSource ? elementId : null;
    const sourceId = isSource ? sourceIdFromNodeId(elementId as string) : null;
    const edgeId = kind === "edge" ? elementId : null;
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(edgeId);
    onSelectRef.current?.({ nodeId, edgeId, sourceId });
  }, []);

  const onSelectionChange = React.useCallback(
    ({ nodes: selNodes, edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
      if (selEdges[0]) {
        announce(selEdges[0].id, "edge");
        return;
      }
      announce(selNodes[0]?.id ?? null, "node");
    },
    [announce],
  );

  const handleNodeClick = React.useCallback(
    (_: React.MouseEvent, node: Node) => announce(node.id, "node"),
    [announce],
  );

  const handleEdgeClick = React.useCallback(
    (_: React.MouseEvent, edge: Edge) => announce(edge.id, "edge"),
    [announce],
  );

  const handlePaneClick = React.useCallback(() => announce(null, "node"), [announce]);

  const handleNodePatch = React.useCallback(
    (nodeId: string, patch: Partial<NodeDefinition>) => {
      const existing = overrides.current.get(nodeId) || {};
      overrides.current.set(nodeId, { ...existing, ...patch });
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...n.data,
                  label: patch.name ?? (n.data as FlowNodeData).label,
                  ownerRole: patch.ownerRole ?? (n.data as FlowNodeData).ownerRole,
                  requireApproval:
                    patch.requireApproval ?? (n.data as FlowNodeData).requireApproval,
                },
              }
            : n,
        ),
      );
      setDirty(true);
    },
    [setDirty],
  );

  const handleEdgeLabelChange = React.useCallback(
    (edgeId: string, label: string) => {
      setEdges((eds) => eds.map((e) => (e.id === edgeId ? { ...e, label } : e)));
      setDirty(true);
    },
    [setDirty],
  );

  const handleDeleteEdge = React.useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      if (selectedEdgeId === edgeId) {
        setSelectedEdgeId(null);
      }
      setDirty(true);
    },
    [selectedEdgeId, setDirty],
  );

  const handleDeleteNode = React.useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      overrides.current.delete(nodeId);
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }
      if (flow && flow.startNodeId === nodeId) {
        setFlow((f) => ({ ...f, startNodeId: "" }));
      }
      setDirty(true);
    },
    [selectedNodeId, flow, setDirty],
  );

  const handleAddNode = React.useCallback(() => {
    const id = `node-${Date.now().toString(36)}`;
    const newNode: Node = {
      id,
      type: "flowNode",
      position: { x: 100, y: 100 + nodes.length * 30 },
      data: {
        label: id,
        ownerRole: "ide:claude",
        requireApproval: false,
      } satisfies FlowNodeData,
    };
    setNodes((nds) => [...nds, newNode]);
    overrides.current.set(id, {
      nodeId: id,
      name: id,
      ownerRole: "ide:claude",
      toolName: "NoOpTool",
      instructions: "",
      transitions: [],
    });
    setDirty(true);
  }, [nodes.length, setDirty]);

  const handleSave = React.useCallback(() => {
    const merged = rfToFlow(flow, nodes, edges, overrides.current);
    onSave(merged);
  }, [flow, nodes, edges, onSave]);

  // Hold the ReactFlow instance so Auto arrange can re-fit the viewport after
  // repositioning. (The component isn't wrapped in ReactFlowProvider, so we
  // grab the instance via onInit rather than the useReactFlow hook.)
  const rfRef = React.useRef<ReactFlowInstance | null>(null);

  const handleAutoArrange = React.useCallback(() => {
    // Diagonal staircase: dagre lays it out vertically, then each step cascades
    // right so the flow reads down-and-to-the-right. Handles stay top/bottom
    // (the flow is downward-dominant), so DIAG maps to the non-LR handle side.
    const dir: LayoutDirection = "DIAG";
    setDirection(dir);
    setNodes((nds) => autoLayout(nds, edges, dir));
    setDirty(true);
    // Re-fit once React Flow has applied the new positions (two frames is
    // enough for the node measurements to settle).
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        rfRef.current?.fitView({ padding: 0.15, duration: 300 });
      });
    });
  }, [edges, setDirty]);

  // Controlled when the host passes the prop, internal otherwise.
  const effSelectedNodeId = selectedNodeIdProp === undefined ? selectedNodeId : selectedNodeIdProp;
  const effSelectedEdgeId = selectedEdgeIdProp === undefined ? selectedEdgeId : selectedEdgeIdProp;

  const selectedNode = React.useMemo<NodeDefinition | null>(() => {
    if (!flow || !effSelectedNodeId) {
      return null;
    }
    const base = (flow.nodes || []).find((n) => n.nodeId === effSelectedNodeId);
    const patch = overrides.current.get(effSelectedNodeId) || {};
    return {
      ...(base || { nodeId: effSelectedNodeId, name: effSelectedNodeId }),
      ...patch,
    };
    // `nodes` is a dep so the panel re-reads overrides.current (a ref) after
    // an inline node edit re-renders the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, effSelectedNodeId, nodes]);

  const selectedEdge = React.useMemo<Edge | null>(() => {
    if (!effSelectedEdgeId) {
      return null;
    }
    return edges.find((e) => e.id === effSelectedEdgeId) || null;
  }, [edges, effSelectedEdgeId]);

  /**
   * The blast radius, drawn. When the host computes it (it has the authoritative draft) we use
   * theirs; otherwise derive it from the selection so the affordance exists either way.
   */
  const effectiveHighlights = React.useMemo<Record<string, HighlightKind>>(() => {
    if (highlights) {
      return highlights;
    }
    if (!effSelectedNodeId) {
      return {};
    }
    return highlightsFor(blastRadius(flow, effSelectedNodeId));
  }, [highlights, flow, effSelectedNodeId]);

  /**
   * Reflect the host's selection into graph state.
   *
   * This is an EFFECT and not a render-time override, and that distinction is load-bearing. Deriving
   * `selected` during render from the controlled prop meant a user's click cancelled itself: React
   * Flow applies its own `select` change to the node, the very next render recomputed `selected`
   * from a prop the host had not updated yet, and the node was deselected before anyone saw it. The
   * canvas must be allowed to select on click; the host's value is applied afterwards, and when the
   * two already agree this is a no-op, so there is no loop.
   */
  React.useEffect(() => {
    if (selectedNodeIdProp === undefined && selectedSourceIdProp === undefined) {
      return; // uncontrolled — React Flow owns selection entirely
    }
    const wantedId = selectedSourceIdProp ? `src:${selectedSourceIdProp}` : selectedNodeIdProp;
    setNodes((nds) => {
      let changed = false;
      const next = nds.map((n) => {
        const want = n.id === wantedId;
        if (!!n.selected !== want) {
          changed = true;
          return { ...n, selected: want };
        }
        return n;
      });
      return changed ? next : nds;
    });
  }, [selectedNodeIdProp, selectedSourceIdProp]);

  React.useEffect(() => {
    if (selectedEdgeIdProp === undefined) {
      return;
    }
    setEdges((eds) => {
      let changed = false;
      const next = eds.map((e) => {
        const want = e.id === selectedEdgeIdProp;
        if (!!e.selected !== want) {
          changed = true;
          return { ...e, selected: want };
        }
        return e;
      });
      return changed ? next : eds;
    });
  }, [selectedEdgeIdProp]);

  /** Nodes as rendered: blast-radius classes only. Selection lives in graph state, above. */
  const renderedNodes = React.useMemo<Node[]>(
    () =>
      nodes.map((n) => {
        const hl = effectiveHighlights[n.id];
        return hl
          ? {
              ...n,
              className: [n.className, `fn-hl-${hl}`].filter(Boolean).join(" "),
            }
          : n;
      }),
    [nodes, effectiveHighlights],
  );

  return (
    <LayoutDirectionContext.Provider value={direction}>
      {/* dir="ltr" is a DELIBERATE opt-out: a node graph is spatial, not textual,
          so mirroring it would move every node, edge and handle for no reader
          benefit. @xyflow already pins .react-flow to ltr; this pins the shell
          chrome to match so the editor cannot end up half-mirrored. */}
      <div className="fn-shell" dir="ltr">
        {!hideToolbar && (
          <div className="fn-toolbar">
            <div className="fn-title">
              {flow?.name || "New flow"}
              {dirty && <span className="fn-dirty"> ●</span>}
            </div>
            <div className="fn-toolbar-actions">
              {toolbarExtras}
              <button
                onClick={handleAutoArrange}
                className="fn-btn"
                title="Tidy the layout (top-to-bottom)"
              >
                ⤵ Auto arrange
              </button>
              <button onClick={handleAddNode} className="fn-btn">
                + Node
              </button>
              {!hideSave && (
                <button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="fn-btn fn-btn-primary"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              )}
            </div>
          </div>
        )}
        {error && (
          <div className="fn-error-banner">
            <span>{error}</span>
          </div>
        )}
        <div className="fn-body">
          <div className="fn-canvas">
            <ReactFlow
              nodes={renderedNodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onSelectionChange={onSelectionChange}
              // Direct click handlers as well as onSelectionChange: the host's callback should not
              // depend on how React Flow batches selection state, and a click that visibly does
              // nothing is the worst failure mode an editor has.
              onNodeClick={handleNodeClick}
              onEdgeClick={handleEdgeClick}
              onPaneClick={handlePaneClick}
              onInit={(instance) => (rfRef.current = instance)}
              deleteKeyCode={["Delete", "Backspace"]}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={16} />
              <Controls />
              <MiniMap pannable zoomable />
            </ReactFlow>
          </div>
          {!hideNodePanel && (
            <NodePanel
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              onNodePatch={handleNodePatch}
              onEdgeLabelChange={handleEdgeLabelChange}
              onDeleteNode={handleDeleteNode}
              onDeleteEdge={handleDeleteEdge}
              flow={flow}
              onFlowPatch={(patch) => {
                setFlow((f) => ({ ...f, ...patch }));
                setDirty(true);
              }}
              thinkers={thinkers}
            />
          )}
        </div>
      </div>
    </LayoutDirectionContext.Provider>
  );
}
